import { yupResolver } from '@hookform/resolvers/yup';
import { number, object, string } from 'yup';

import { IYearMonth, toDay } from '~/utils/date/date';
import { ICeExemption } from '~/services/api/communityEngagement/types';
import {
  CE_COMMENT_TOO_LONG,
  CE_EXPIRATION_BEFORE_EFFECTIVE,
  CE_MAX_COMMENT,
} from '~/ui/constants/communityEngagement';
import { requiredField } from '~/ui/constants/errorMessages';

import getDefaultEffectiveDate from './getDefaultEffectiveDate';

export interface IExemptionFormValues {
  exemptionCategoryId: number | '';
  effectiveDate: string;
  expirationDate: string | null;
  comment: string;
}

export interface IExemptionFormContext {
  isAutomatic: boolean;
}

export const getDefaultValues = (
  exemption: ICeExemption | null,
  selected: IYearMonth,
): IExemptionFormValues =>
  exemption
    ? {
        exemptionCategoryId: exemption.category.id,
        effectiveDate: exemption.effectiveDate,
        expirationDate: exemption.expirationDate,
        comment: exemption.comment ?? '',
      }
    : {
        exemptionCategoryId: '',
        effectiveDate: getDefaultEffectiveDate(new Date(), selected),
        expirationDate: '',
        comment: '',
      };

const schema = object().shape({
  exemptionCategoryId: number()
    .transform((value, original) => (original === '' ? undefined : value))
    .required(requiredField),
  effectiveDate: string().required(requiredField),
  expirationDate: string()
    .nullable()
    .when('$isAutomatic', {
      is: true,
      otherwise: field =>
        field
          .required(requiredField)
          .test('after-effective', CE_EXPIRATION_BEFORE_EFFECTIVE, (value, { parent }) => {
            const effective = toDay(parent.effectiveDate ?? '');
            const expiration = toDay(value ?? '');
            return !effective || !expiration || expiration > effective;
          }),
    }),
  comment: string().trim().max(CE_MAX_COMMENT, CE_COMMENT_TOO_LONG),
});

export const exemptionResolver = yupResolver(schema);
